// src/admin/pages/AdminSecurity.jsx
import React, { useState, useEffect } from "react";
import { authService } from "../../../services/api";
import TabBlockView from "../../components/TabBlockView";

// ─── Change Password Tab ───────────────────────────────────────────────────────
const ChangePasswordTab = () => {
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false, uppercase: false, lowercase: false,
    number: false, symbol: false, noCommon: true, noSequential: true, noRepeated: true,
  });
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false, newPassword: false, confirmPassword: false,
  });

  const commonPasswords = [
    "password","123456","123456789","qwerty","abc123","password123",
    "admin","letmein","welcome","monkey","1234567890","password1",
    "123123","111111","qwertyuiop","asdfgh","zxcvbn","password12",
  ];

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const hasSequentialChars = (password) => {
    const sequences = [
      "abcdefghijklmnopqrstuvwxyz","ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      "0123456789","qwertyuiopasdfghjklzxcvbnm",
    ];
    for (let i = 0; i < password.length - 2; i++) {
      const substr = password.slice(i, i + 4).toLowerCase();
      if (sequences.some((seq) => seq.includes(substr))) return true;
    }
    return false;
  };

  const hasRepeatedChars = (password) => {
    for (let i = 0; i < password.length - 2; i++) {
      if (password[i] === password[i + 1] && password[i + 1] === password[i + 2]) return true;
    }
    return false;
  };

  const calculatePasswordStrength = (password) => {
    const reqs = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password),
      noCommon: !commonPasswords.includes(password.toLowerCase()),
      noSequential: !hasSequentialChars(password),
      noRepeated: !hasRepeatedChars(password),
    };
    setPasswordRequirements(reqs);
    setPasswordStrength(Math.round((Object.values(reqs).filter(Boolean).length / 8) * 100));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "newPassword") calculatePasswordStrength(value);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const getStrengthColor = () => passwordStrength < 40 ? "#dc3545" : passwordStrength < 75 ? "#ffc107" : "#28a745";
  const getStrengthText  = () => passwordStrength < 40 ? "Слабкий" : passwordStrength < 75 ? "Середній" : "Надійний";

  const validateForm = () => {
    if (!formData.oldPassword) return "Введіть поточний пароль";
    if (!formData.newPassword) return "Введіть новий пароль";
    if (!passwordRequirements.length) return "Новий пароль повинен містити принаймні 8 символів";
    if (!passwordRequirements.uppercase) return "Новий пароль повинен містити принаймні одну велику літеру";
    if (!passwordRequirements.lowercase) return "Новий пароль повинен містити принаймні одну малу літеру";
    if (!passwordRequirements.number) return "Новий пароль повинен містити принаймні одну цифру";
    if (!passwordRequirements.symbol) return "Новий пароль повинен містити принаймні один спеціальний символ";
    if (!passwordRequirements.noCommon) return "Цей пароль занадто поширений";
    if (!passwordRequirements.noSequential) return "Пароль не повинен містити послідовні символи";
    if (!passwordRequirements.noRepeated) return "Пароль не повинен містити більше двох однакових символів підряд";
    if (formData.newPassword !== formData.confirmPassword) return "Паролі не співпадають";
    if (formData.oldPassword === formData.newPassword) return "Новий пароль повинен відрізнятися від поточного";
    if (passwordStrength < 75) return "Пароль занадто слабкий";
    return null;
  };

  const emptyReqs = { length: false, uppercase: false, lowercase: false, number: false, symbol: false, noCommon: true, noSequential: true, noRepeated: true };

  const resetForm = () => {
    setFormData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordStrength(0);
    setPasswordRequirements(emptyReqs);
    showMessage("info", "Форму очищено");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { showMessage("error", err); return; }
    try {
      setLoading(true);
      await authService.changePassword(formData.oldPassword, formData.newPassword);
      showMessage("success", "Пароль успішно змінено!");
      setFormData({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordStrength(0);
      setPasswordRequirements(emptyReqs);
    } catch (err) {
      showMessage("error", err.message || "Помилка при зміні пароля. Перевірте правильність поточного пароля.");
    } finally {
      setLoading(false);
    }
  };

  const reqItems = [
    { key: "length",       label: "Мінімум 8 символів" },
    { key: "uppercase",    label: "Велика літера (A-Z)" },
    { key: "lowercase",    label: "Мала літера (a-z)" },
    { key: "number",       label: "Цифра (0-9)" },
    { key: "symbol",       label: "Спеціальний символ (!@#$%^&*)" },
    { key: "noCommon",     label: "Не поширений пароль" },
    { key: "noSequential", label: "Без послідовних символів" },
    { key: "noRepeated",   label: "Без повторень символів" },
  ];

  return (
    <div>
      {message && <MessageBanner message={message} />}

      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>Змінити пароль</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          {(["oldPassword", "newPassword", "confirmPassword"] as const).map((field) => (
            <div key={field} style={styles.formGroup}>
              <label style={styles.label} htmlFor={field}>
                {field === "oldPassword" ? "Поточний пароль:" : field === "newPassword" ? "Новий пароль:" : "Підтвердити новий пароль:"}
              </label>
              <div style={styles.passwordInputContainer}>
                <input
                  style={styles.passwordInput}
                  type={showPasswords[field] ? "text" : "password"}
                  id={field} name={field}
                  placeholder={field === "oldPassword" ? "Введіть поточний пароль" : field === "newPassword" ? "Введіть новий пароль" : "Повторіть новий пароль"}
                  value={formData[field]}
                  onChange={handleInputChange}
                  required
                />
                <button type="button" style={styles.toggleButton} onClick={() => togglePasswordVisibility(field)}>
                  {showPasswords[field] ? "🙈" : "👁️"}
                </button>
              </div>

              {field === "newPassword" && formData.newPassword && (
                <>
                  <div style={styles.strengthContainer}>
                    <div style={styles.strengthBar}>
                      <div style={{ ...styles.strengthFill, width: `${passwordStrength}%`, backgroundColor: getStrengthColor() }} />
                    </div>
                    <span style={{ color: getStrengthColor(), fontSize: "0.9rem", fontWeight: 500 }}>
                      {getStrengthText()} ({passwordStrength}%)
                    </span>
                  </div>
                  <div style={styles.requirementsContainer}>
                    {reqItems.map(({ key, label }) => (
                      <div key={key} style={styles.requirementItem}>
                        <span style={passwordRequirements[key] ? styles.checkmark : styles.cross}>
                          {passwordRequirements[key] ? "✓" : "✗"}
                        </span>
                        {label}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}

          <div style={styles.buttonContainer}>
            <button type="button" style={styles.resetButton} onClick={resetForm} disabled={loading}>Очистити форму</button>
            <button type="submit" style={{ ...styles.button, opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }} disabled={loading}>
              {loading ? "Збереження..." : "Змінити пароль"}
            </button>
          </div>
        </form>
      </div>

      <div style={styles.infoContainer}>
        <h3 style={styles.infoTitle}>Вимоги до безпечного пароля:</h3>
        <ul style={styles.infoList}>
          <li>Мінімум 8 символів</li>
          <li>Принаймні одна велика літера, мала літера, цифра та спеціальний символ</li>
          <li>Не використовуйте поширені паролі або послідовні/повторювані символи</li>
          <li>Новий пароль повинен відрізнятися від поточного</li>
        </ul>
      </div>
    </div>
  );
};

// ─── Email Management Tab ──────────────────────────────────────────────────────
const EmailManagementTab = () => {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [editingEmail, setEditingEmail] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingEmails, setFetchingEmails] = useState(true);

  useEffect(() => { fetchEmails(); }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const fetchEmails = async () => {
    try {
      setFetchingEmails(true);
      const response = await authService.getEmails();
      setEmails(response.data.emails || []);
    } catch {
      showMessage("error", "Помилка при завантаженні списку email-адрес");
    } finally {
      setFetchingEmails(false);
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) { showMessage("error", "Введіть email-адресу"); return; }
    if (!validateEmail(newEmail)) { showMessage("error", "Невірний формат email-адреси"); return; }
    if (emails.includes(newEmail)) { showMessage("error", "Ця email-адреса вже існує в списку"); return; }
    try {
      setLoading(true);
      await authService.addEmail(newEmail);
      showMessage("success", "Email-адресу успішно додано!");
      setNewEmail("");
      await fetchEmails();
    } catch (err) {
      showMessage("error", err.response?.data?.error || "Помилка при додаванні email-адреси");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async (oldEmail) => {
    if (!editValue.trim()) { showMessage("error", "Введіть нову email-адресу"); return; }
    if (!validateEmail(editValue)) { showMessage("error", "Невірний формат email-адреси"); return; }
    if (oldEmail === editValue) { setEditingEmail(null); return; }
    if (emails.includes(editValue)) { showMessage("error", "Ця email-адреса вже існує в списку"); return; }
    try {
      setLoading(true);
      await authService.updateEmail(oldEmail, editValue);
      showMessage("success", "Email-адресу успішно оновлено!");
      setEditingEmail(null);
      setEditValue("");
      await fetchEmails();
    } catch (err) {
      showMessage("error", err.response?.data?.error || "Помилка при оновленні email-адреси");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async (email) => {
    if (emails.length <= 1) { showMessage("error", "Неможливо видалити останню email-адресу адміністратора"); return; }
    if (!window.confirm(`Ви впевнені, що хочете видалити email-адресу "${email}"?`)) return;
    try {
      setLoading(true);
      await authService.deleteEmail(email);
      showMessage("success", "Email-адресу успішно видалено!");
      await fetchEmails();
    } catch (err) {
      showMessage("error", err.response?.data?.error || "Помилка при видаленні email-адреси");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {message && <MessageBanner message={message} />}

      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>Додати нову email-адресу</h2>
        <form onSubmit={handleAddEmail} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="newEmail">Email-адреса:</label>
            <input
              style={styles.input} type="email" id="newEmail"
              placeholder="admin@example.com" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)} disabled={loading}
            />
          </div>
          <button type="submit" style={{ ...styles.button, opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer", alignSelf: "flex-start" }} disabled={loading}>
            {loading ? "Додавання..." : "Додати email"}
          </button>
        </form>
      </div>

      <div style={styles.listContainer}>
        <h2 style={styles.listTitle}>Поточні адміністратори ({emails.length})</h2>
        {fetchingEmails ? (
          <div style={styles.loadingContainer}><p>Завантаження...</p></div>
        ) : emails.length === 0 ? (
          <div style={styles.emptyState}><p>Немає email-адрес адміністраторів</p></div>
        ) : (
          <div style={styles.emailsList}>
            {emails.map((email, index) => (
              <div key={index} style={styles.emailItem}>
                {editingEmail === email ? (
                  <div style={styles.editContainer}>
                    <input style={styles.editInput} type="email" value={editValue}
                      onChange={(e) => setEditValue(e.target.value)} disabled={loading} autoFocus />
                    <div style={styles.editButtons}>
                      <button style={styles.saveButton} onClick={() => handleUpdateEmail(email)} disabled={loading}>✓ Зберегти</button>
                      <button style={styles.cancelButton} onClick={() => setEditingEmail(null)} disabled={loading}>✗ Скасувати</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.emailInfo}>
                      <span style={styles.emailText}>{email}</span>
                      {emails.length === 1 && <span style={styles.lastAdminBadge}>Останній адмін</span>}
                    </div>
                    <div style={styles.actionButtons}>
                      <button style={styles.editButton} onClick={() => { setEditingEmail(email); setEditValue(email); }} disabled={loading}>✏️ Редагувати</button>
                      <button
                        style={{ ...styles.deleteButton, opacity: emails.length === 1 ? 0.5 : 1, cursor: emails.length === 1 ? "not-allowed" : "pointer" }}
                        onClick={() => handleDeleteEmail(email)} disabled={loading || emails.length === 1}>
                        🗑️ Видалити
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.infoContainer}>
        <h3 style={styles.infoTitle}>Інформація про управління email-адресами:</h3>
        <ul style={styles.infoList}>
          <li>Лише користувачі з цими email-адресами можуть увійти як адміністратори</li>
          <li>Для входу потрібен як пароль адміністратора, так і автентифікація через Google</li>
          <li>Неможливо видалити останню email-адресу адміністратора з міркувань безпеки</li>
        </ul>
      </div>
    </div>
  );
};

// ─── Shared message banner ─────────────────────────────────────────────────────
const MessageBanner = ({ message }) => (
  <div style={{
    ...styles.message,
    backgroundColor: message.type === "success" ? "#d4edda" : message.type === "error" ? "#f8d7da" : message.type === "info" ? "#cce5ff" : "#fff3cd",
    color: message.type === "success" ? "#155724" : message.type === "error" ? "#721c24" : message.type === "info" ? "#004085" : "#856404",
  }}>
    {message.text}
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const AdminSecurity = () => (
  <div style={styles.pageContainer}>
    <h1 style={styles.pageTitle}>Безпека</h1>
    <TabBlockView
      tabs={[
        { name: "Пароль", content: <ChangePasswordTab /> },
        { name: "Пошти адміністраторів", content: <EmailManagementTab /> },
      ]}
    />
  </div>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  formContainer: {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  formTitle: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    maxWidth: "500px",
  },
  formGroup: { display: "flex", flexDirection: "column" },
  label: { marginBottom: "0.5rem", fontWeight: 600 },
  input: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
    fontSize: "1rem",
  },
  passwordInputContainer: { position: "relative", display: "flex", alignItems: "center" },
  passwordInput: {
    padding: "0.8rem",
    paddingRight: "3rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
    fontSize: "1rem",
    width: "100%",
  },
  toggleButton: { position: "absolute", right: "0.5rem", background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", padding: "0.2rem" },
  strengthContainer: { marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" },
  strengthBar: { flex: 1, height: "8px", backgroundColor: "#e9ecef", borderRadius: "4px", overflow: "hidden" },
  strengthFill: { height: "100%", transition: "width 0.3s ease" },
  requirementsContainer: { marginTop: "0.5rem", padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "4px", border: "1px solid #e9ecef" },
  requirementItem: { display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem", marginBottom: "0.25rem" },
  checkmark: { color: "#28a745", fontWeight: "bold" },
  cross: { color: "#dc3545", fontWeight: "bold" },
  button: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  resetButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  buttonContainer: { display: "flex", justifyContent: "flex-start", marginTop: "1rem" },
  message: { padding: "1rem", marginBottom: "1rem", borderRadius: "4px" },
  infoContainer: { padding: "1.5rem", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef" },
  infoTitle: { marginBottom: "1rem", fontSize: "1.2rem", color: "var(--secondary-color)" },
  infoList: { margin: 0, paddingLeft: "1.5rem", color: "#6c757d" },
  // Email list styles
  listContainer: { marginBottom: "2rem", padding: "1.5rem", backgroundColor: "#f9f9f9", borderRadius: "8px" },
  listTitle: { marginBottom: "1rem", fontSize: "1.3rem", color: "var(--secondary-color)" },
  loadingContainer: { padding: "2rem", textAlign: "center", color: "#6c757d" },
  emptyState: { padding: "2rem", textAlign: "center", color: "#6c757d", backgroundColor: "#fff", borderRadius: "4px", border: "1px solid #e9ecef" },
  emailsList: { display: "flex", flexDirection: "column", gap: "0.75rem" },
  emailItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem", backgroundColor: "#fff", borderRadius: "4px", border: "1px solid #e9ecef" },
  emailInfo: { display: "flex", alignItems: "center", gap: "1rem", flex: 1 },
  emailText: { fontSize: "1rem", color: "#212529", fontWeight: 500 },
  lastAdminBadge: { padding: "0.25rem 0.5rem", backgroundColor: "#ffc107", color: "#856404", borderRadius: "4px", fontSize: "0.75rem", fontWeight: 600 },
  actionButtons: { display: "flex", gap: "0.5rem" },
  editButton: { padding: "0.5rem 1rem", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontSize: "0.9rem", cursor: "pointer" },
  deleteButton: { padding: "0.5rem 1rem", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontSize: "0.9rem", cursor: "pointer" },
  editContainer: { display: "flex", gap: "1rem", alignItems: "center", width: "100%" },
  editInput: { flex: 1, padding: "0.6rem", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontSize: "1rem" },
  editButtons: { display: "flex", gap: "0.5rem" },
  saveButton: { padding: "0.5rem 1rem", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontSize: "0.9rem", cursor: "pointer", whiteSpace: "nowrap" },
  cancelButton: { padding: "0.5rem 1rem", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontSize: "0.9rem", cursor: "pointer", whiteSpace: "nowrap" },
};

export default AdminSecurity;